import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import { Head, Loader, Nav, Social, Footer, ThemeToggle } from '@components';
import ScrollToTop from './ScrollToTop';
import { GlobalStyle, theme } from '@styles';
import { getTheme, defaultTheme } from '@styles/themes';

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  animation: fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Layout = ({ children, location }) => {
  const isHome = location.pathname === '/';
  const [isLoading, setIsLoading] = useState(isHome);

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Helper function to determine if a color is dark
  const isDarkTheme = color => {
    // Remove # if present
    const hex = color.replace('#', '');
    // Convert to RGB
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    // Return true if dark (luminance < 0.5)
    return luminance < 0.5;
  };

  // Apply initial theme to CSS custom properties
  useEffect(() => {
    const currentDefaultTheme = defaultTheme; // Get current default theme
    const selectedTheme = getTheme(currentDefaultTheme);
    const root = document.documentElement;

    // Map theme properties to your existing CSS variables
    const cssVariableMap = {
      // Background colors
      '--navy': selectedTheme.background,
      '--light-navy': selectedTheme.backgroundSecondary,
      '--lightest-navy': selectedTheme.backgroundTertiary,
      '--dark-navy': selectedTheme.backgroundAccent,

      // Text colors
      '--lightest-slate': selectedTheme.text,
      '--slate': selectedTheme.textSecondary,
      '--light-slate': selectedTheme.textTertiary,
      '--dark-slate': selectedTheme.textMuted,

      // Accent colors
      '--white-new': selectedTheme.accent,
      '--white-new-tint': selectedTheme.accentTint,
      '--pink': selectedTheme.accentSecondary,
      '--blue': selectedTheme.accentTertiary,

      // Utility colors
      '--white': selectedTheme.white,
      '--pure-white': selectedTheme.pureWhite,
      '--black': selectedTheme.black,
      '--navy-shadow': selectedTheme.shadow,

      // Additional colors used in components (theme-aware)
      '--green': selectedTheme.accent, // Maps to primary accent for consistency
      '--green-tint': selectedTheme.accentTint, // Maps to accent tint

      // Dynamic navbar background (theme-adaptive translucent version of background)
      '--nav-bg': hexToRgba(selectedTheme.background, 0.85),

      // Featured image overlay - use navy for screen blend mode
      '--featured-overlay': selectedTheme.primary,

      // Logo fill color - adaptive based on background brightness
      '--logo-fill': isDarkTheme(selectedTheme.background)
        ? '#0a192f' // For dark themes, use original navy color (original behavior)
        : selectedTheme.text, // For light themes, use text color for visibility
    };

    // Apply all CSS variables to root
    Object.entries(cssVariableMap).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
  }, []); // Empty dependency - will read current defaultTheme each time it's imported

  // Sets target="_blank" rel="noopener noreferrer" on external links
  const handleExternalLinks = () => {
    const allLinks = Array.from(document.querySelectorAll('a'));
    if (allLinks.length > 0) {
      allLinks.forEach(link => {
        if (link.host !== window.location.host) {
          link.setAttribute('rel', 'noopener noreferrer');
          link.setAttribute('target', '_blank');
        }
      });
    }
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (location.hash) {
      const id = location.hash.substring(1); // location.hash without the '#'
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView();
          el.focus();
        }
      }, 0);
    }

    handleExternalLinks();
  }, [isLoading]);

  return (
    <>
      <Head />

      <div id="root">
        <ThemeProvider theme={theme}>
          <GlobalStyle />

          <a className="skip-to-content" href="#content">
            Skip to Content
          </a>

          {isLoading && isHome ? (
            <Loader finishLoading={() => setIsLoading(false)} />
          ) : (
            <StyledContent>
              <Nav isHome={isHome} />
              <Social isHome={isHome} />

              <div id="content">
                {children}
                <Footer />
              </div>
            </StyledContent>
          )}
          <ThemeToggle />
          <ScrollToTop />
        </ThemeProvider>
      </div>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired,
};

export default Layout;
